import type { Glossary } from '@ps/web-martech-cms-types';
import { debounce, findLastIndex, sortBy, uniq } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CMSHTML } from '@swf/cms';

import styles from './CMSGlossary.scss';

const TermElementPrefix = 'dt-';
const WindowResizeDebounceMs = 200;
const TopPositionOffset = 40;

const CMSGlossary: React.FC<Glossary> = ({ uid, items }) => {
  const glossaryContainerRef = useRef<HTMLDListElement>(null);

  const glossaryItems = useMemo(() => sortBy(items, 'term'), [items]);
  const menuItems = useMemo(() => uniq(glossaryItems.map((item) => item.term[0])), [glossaryItems]);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>(menuItems[0]);
  const termScrollTopPositions = useRef<number[]>([]);

  useEffect(() => {
    const buildTermScrollTopPositions = debounce(() => {
      const glossaryContainer = glossaryContainerRef.current;
      if (!glossaryContainer) {
        return;
      }

      const currentScrollTopValue = document.documentElement.scrollTop;
      // ScrollTop temporary assignment to 0 to build correct scroll positions of all first of char items.
      document.documentElement.scrollTop = 0;
      termScrollTopPositions.current = menuItems.map((item) => {
        const currentItemScrollTop =
          glossaryContainer.querySelector(`.${TermElementPrefix}${item}`)?.getBoundingClientRect()
            .top || 0;
        return Math.ceil(currentItemScrollTop - TopPositionOffset);
      });
      document.documentElement.scrollTop = currentScrollTopValue;
    }, WindowResizeDebounceMs);

    buildTermScrollTopPositions();
    window.addEventListener('resize', buildTermScrollTopPositions);

    return () => window.removeEventListener('resize', buildTermScrollTopPositions);
  }, [menuItems]);

  const onScroll = useCallback(() => {
    const currentScrollVal = document.documentElement.scrollTop || 0;
    let index = findLastIndex(
      termScrollTopPositions.current,
      (scrollTop) => currentScrollVal >= scrollTop
    );
    index = index === -1 ? 0 : index;
    setSelectedMenuItem(menuItems[index]);
  }, [menuItems]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const onMenuClick = (index: number) => {
    document.documentElement.scrollTo(0, termScrollTopPositions.current[index]);
  };

  return (
    <div className={styles.glossary} id={uid}>
      <div className={styles.alphabeticalNavIndex}>
        {menuItems.map((item, index) => (
          <button
            className={selectedMenuItem === item ? styles.activeIndex : ''}
            key={`${index}`}
            onClick={() => {
              onMenuClick(index);
            }}
          >
            {item}
          </button>
        ))}
      </div>
      <dl ref={glossaryContainerRef} className={styles.glossaryContent} onScroll={onScroll}>
        {glossaryItems.map(({ term, definition }, index) => (
          <React.Fragment key={`${index}`}>
            <dt className={`${styles.glossaryTerm} ${TermElementPrefix}${term[0]}`}>
              <b>{term}</b>
            </dt>
            <CMSHTML className={styles.glossaryDefinition} tag="dd" content={definition} />
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

export default CMSGlossary;
